/**
 * 교재 "파일 재업로드로 수정" 화면(교사·원장 공용). 바꾼 종류의 PDF만 고르면 나머지는 직전 버전 본문을 재사용한다.
 * 미리보기(서버, 쓰기 없음) → 변경·삭제 지점 수 확인 → 한 번 확인하고 새 버전 등록(원장 별도 승인 없음).
 * 화면은 판정하지 않는다: 같은 문항 판단·권한·실패 시 기존 버전 유지는 서버가 한다.
 * 학교·학년은 고르지 않으면 이전 버전의 대상이 그대로 새 버전으로 옮겨 가고, 고르면 그 쌍으로 바뀐다.
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import { evidenceReasonText, toBase64, type MultiSemantics } from '../core/pdf-import.js';
import { mountSchoolGradePicker } from './school-grade-picker.js';

interface SetRow { setId: string; latestVersion: number; sections: string[]; canReuseFiles: boolean }
interface EvCandidate { section: string; number: number; stem: string; options: string[]; correctNo: number; sourceQuote: string | null; teacherPicked?: boolean }
interface EvException { section: string; number: number; reason: string | null; pickable: boolean }
interface EvReview { enabled: boolean; previousVersionHadEvidence: boolean; generatorChanged?: boolean; candidatesTotal: number; reusedUnchanged: number; toReview: number }
interface Diff { available: boolean; total?: number; unchanged?: number; changedOrAdded?: { section: string; number: number }[]; removed?: { section: string; number: number }[] }

const FIELDS = [['questionPdf', '문제지', 'reup-question'], ['answerPdf', '정답지', 'reup-answer'], ['explanationPdf', '해설지', 'reup-explanation']] as const;

/** 서버 응답 → 안내 문구(순수). 변경/삭제/미변경 수와 재사용한 파일을 그대로 보여 준다 */
export function diffSummary(diff: Diff | undefined, reused: string[]): string {
  const names = new Map<string, string>(FIELDS.map(([f, label]) => [f, label]));
  const re = reused.length > 0 ? ` 올리지 않은 ${reused.map((r) => names.get(r) ?? r).join('·')}은(는) 이전 버전 그대로 써요.` : '';
  if (diff === undefined || !diff.available) return `이전 버전과 비교할 기록이 없어요.${re}`;
  return `전체 ${diff.total}개 지점 중 바뀌지 않음 ${diff.unchanged}, 바뀌었거나 새로 생김 ${diff.changedOrAdded?.length ?? 0}, 삭제 ${diff.removed?.length ?? 0}.${re}`;
}

/** 근거 재검토 안내(순수). 변경 없는 후보는 재사용, 바뀌었거나 교사가 고른 지점만 다시 보여 준다 */
export function evidenceReviewText(r: EvReview | undefined): string {
  if (r === undefined || !r.enabled) return '근거 문제는 이번 버전에 함께 등록하지 않아요(켜지 않음). 학생에게는 "근거 자료 없음"으로 보여요.';
  if (r.generatorChanged === true) return `근거 문제를 만드는 규칙이 새로워졌어요. 같은 파일이어도 후보 ${r.toReview}개를 모두 다시 확인해야 새 버전으로 등록돼요. 확인해서 등록하기 전까지 이전 버전의 근거 문제와 학생 기록은 그대로예요. 오답 보기의 타당성은 아직 검증되지 않았어요.`;
  const prev = r.previousVersionHadEvidence ? `이전 버전과 같은 후보 ${r.reusedUnchanged}개는 다시 확인하지 않아도 돼요. ` : '이전 버전에는 근거 문제가 없어서 후보 전체가 새로 등록돼요. ';
  return `${prev}다시 볼 후보 ${r.toReview}개(바뀌었거나 새로 고른 지점). 오답 보기의 타당성은 아직 검증되지 않았어요.`;
}

export function reuploadFailText(status: number, body: Record<string, any>): string {
  const e = String(body['error'] ?? '');
  if (e === 'preview_has_blocking_issues') return `확인이 필요한 항목이 있어 새 버전을 만들지 않았어요. 기존 버전(v${String(body['existingVersionKept'] ?? '')})은 그대로 쓰여요.`;
  if (e === 'registration_stopped') return `일부 구역에서 멈췄어요. 기존 버전은 그대로 쓰이고, 같은 파일로 다시 누르면 이어서 처리해요.`;
  if (e === 'file_required') return String(body['message'] ?? '세 파일을 모두 올려 주세요.');
  if (e === 'pdf_needs_ocr') return String(body['message'] ?? '스캔 PDF는 지원하지 않아요.');
  if (e === 'pdf_unreadable') return `PDF를 읽지 못했어요(${String(body['field'] ?? '')}). 기존 버전은 그대로예요.`;
  if (e === 'content_version_conflict') return String(body['note'] ?? '같은 버전이 다른 근거 설정으로 이미 있어요. 기존 내용은 그대로예요.');
  if (e === 'evidence_pick_not_allowed') return `${String(body['section'] ?? '')} ${String(body['number'] ?? '')}번은 유형을 고를 수 없는 지점이에요. 선택을 풀고 다시 시도해 주세요.`;
  if (status === 404) return '이 교재를 찾을 수 없거나 담당 범위 밖이에요.';
  if (status === 403) return '이 계정은 할 수 없는 작업이에요.';
  if (status === 401) return '다시 로그인해 주세요.';
  return `처리하지 못했어요(${e || status}). 기존 버전은 그대로 쓰여요.`;
}

export function mountPdfReupload(t: Transport): HTMLElement {
  const gen = currentGen();
  const live = () => gen === currentGen();
  let busy = false;
  let previewed = false;
  const msg = h('p', { role: 'status', 'data-testid': 'reup-message' });
  const sel = h('select', { id: 'reup-set', 'data-testid': 'reup-set', 'aria-label': '수정할 교재' }, h('option', { value: '' }, '교재 선택'));
  const inputs = FIELDS.map(([field, label, id]) => ({ field, input: h('input', { type: 'file', id, accept: 'application/pdf,.pdf', 'data-testid': id, onchange: () => { previewed = false; confirm.checked = false; } }), label, id }));
  const bulkSel = h('select', { id: 'reup-bulk', 'data-testid': 'reup-bulk', 'aria-label': '복수 정답 전체의 뜻' },
    h('option', { value: '' }, '복수 정답 없음/직접 정함'), h('option', { value: 'any_one_accepted' }, '하나만 골라도 정답'), h('option', { value: 'all_required' }, '인정 정답을 전부 골라야 정답'));
  // 근거 문제: 켜면 해설 기반 후보를 새 버전에 함께 등록한다. 확인 필요 지점은 교사가 유형 후보를 고르면 보완된다(선택, 직접 입력 없음).
  const picks = new Map<string, string>();
  const autoEv = h('input', { type: 'checkbox', checked: true, 'data-testid': 'reup-auto-evidence', onchange: () => { previewed = false; confirm.checked = false; } });
  const picker = mountSchoolGradePicker(t, live, () => undefined, 'reup');
  const confirm = h('input', { type: 'checkbox', 'data-testid': 'reup-confirm' });
  const out = h('div', { 'data-testid': 'reup-preview' });
  const previewBtn = h('button', { type: 'button', class: 'secondary', 'data-testid': 'reup-preview-btn', onclick: () => void send(false) }, '변경 미리보기');
  const regBtn = h('button', { type: 'button', class: 'primary', 'data-testid': 'reup-register', onclick: () => void send(true) }, '확인하고 새 버전 등록');

  void (async () => {
    const r = await t.request('GET', '/admin/content/pdf-imports');
    if (!live() || r.kind !== 'http' || r.status !== 200) return;
    for (const s of r.body['sets'] as SetRow[]) sel.append(h('option', { value: s.setId }, `${s.setId} · v${s.latestVersion}${s.sections.length > 0 ? ` · ${s.sections.length}개 구역` : ''}`));
  })();
  sel.addEventListener('change', () => { picks.clear(); previewed = false; confirm.checked = false; clear(out); });

  async function body(confirmed: boolean): Promise<Record<string, unknown>> {
    const b: Record<string, unknown> = {};
    for (const x of inputs) { const f = x.input.files?.[0]; if (f !== undefined) b[x.field] = toBase64(new Uint8Array(await f.arrayBuffer())); }
    if (bulkSel.value === 'any_one_accepted' || bulkSel.value === 'all_required') b['multiAnswerSemantics'] = bulkSel.value as MultiSemantics;
    const pairs = picker.pairs();
    if (pairs.length > 0) b['pairs'] = pairs;
    if (autoEv.checked) {
      b['autoEvidence'] = true;
      if (picks.size > 0) b['evidencePicks'] = [...picks].map(([k, category]) => { const i = k.lastIndexOf('#'); return { section: k.slice(0, i), number: Number(k.slice(i + 1)), category }; });
    }
    if (confirmed) { b['confirmed'] = true; b['confirmAnswerKey'] = true; }
    return b;
  }

  function evidenceNode(b: Record<string, any>): HTMLElement {
    const ev = b['evidenceLinks'] as { summary: { total: number; auto: number; needsReview: number; notApplicable: number }; exceptions: EvException[]; candidates: EvCandidate[]; categories: { id: string; label: string }[] } | undefined;
    const node = h('section', { 'data-testid': 'reup-evidence' }, h('h3', {}, '근거 문제'));
    node.append(h('p', { 'data-testid': 'reup-evidence-review' }, evidenceReviewText(b['evidenceReview'] as EvReview | undefined)));
    if (ev === undefined || !autoEv.checked) return node;
    node.append(h('p', { 'data-testid': 'reup-evidence-summary' }, `전체 ${ev.summary.total}지점 · 후보 ${ev.summary.auto} · 확인 필요 ${ev.summary.needsReview} · 근거 자료 없음(어휘·추론) ${ev.summary.notApplicable}`));
    if (ev.candidates.length > 0) {
      node.append(h('details', { 'data-testid': 'reup-evidence-candidates' }, h('summary', {}, `다시 볼 후보 ${ev.candidates.length}개`),
        h('ul', {}, ...ev.candidates.map((c) => h('li', {}, `${c.section} ${c.number}번${c.teacherPicked === true ? '(내가 고른 유형)' : ''} · ${c.stem} → ${c.options[c.correctNo - 1] ?? ''}${c.sourceQuote !== null ? ` · 출처: ${c.sourceQuote}` : ''}`)))));
    }
    const pickable = ev.exceptions.filter((x) => x.pickable);
    const rest = ev.exceptions.length - pickable.length;
    if (ev.exceptions.length > 0) {
      node.append(h('div', { 'data-testid': 'reup-evidence-exceptions' },
        h('p', {}, `확인 필요 ${ev.exceptions.length}개는 고르지 않으면 "근거 자료 없음"으로 남아요(통과로 세지 않아요). 유형을 고르면 다음 미리보기에서 후보가 돼요. 고르는 것은 선택이고 직접 쓰지 않아도 돼요.`),
        ...(pickable.length === 0 ? [] : [h('ul', {}, ...pickable.map((x) => {
          const key = `${x.section}#${x.number}`;
          const s = h('select', { 'data-testid': `reup-pick-${x.number}`, 'aria-label': `${x.section} ${x.number}번 근거 유형`, onchange: (e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            if (v === '') picks.delete(key); else picks.set(key, v);
            previewed = false; confirm.checked = false; msg.textContent = '근거 유형을 바꿨어요. 변경 미리보기를 다시 눌러 확인해 주세요.';
          } }, h('option', { value: '' }, '근거 없이 두기'), ...ev.categories.map((c) => h('option', { value: c.id, ...(picks.get(key) === c.id ? { selected: true } : {}) }, c.label)));
          return h('li', {}, `${x.section} ${x.number}번 · ${evidenceReasonText(x.reason)} `, s);
        }))]),
        rest > 0 ? h('p', {}, `${rest}개는 복수 정답 등으로 유형을 고를 수 없어요. 근거 자료 없음으로 남아요.`) : null));
    }
    return node;
  }

  async function send(confirmed: boolean): Promise<void> {
    if (busy) return; // 중복 제출 방지
    if (sel.value === '') { msg.textContent = '수정할 교재를 선택해 주세요.'; return; }
    if (!inputs.some((x) => x.input.files?.[0] !== undefined)) { msg.textContent = '바꾼 PDF를 하나 이상 선택해 주세요.'; return; }
    if (confirmed && !previewed) { msg.textContent = '먼저 변경 미리보기를 확인해 주세요.'; return; }
    if (confirmed && !confirm.checked) { msg.textContent = '변경 내용을 확인했다는 체크가 필요해요.'; return; }
    busy = true; previewBtn.disabled = true; regBtn.disabled = true;
    msg.textContent = confirmed ? '새 버전을 등록하는 중…' : 'PDF를 읽고 비교하는 중…';
    try {
      const r = await t.request('POST', `/admin/content/pdf-imports/${encodeURIComponent(sel.value)}/reupload`, { body: await body(confirmed) });
      if (!live()) return;
      if (r.kind !== 'http') { msg.textContent = '결과를 받지 못했어요. 같은 파일로 다시 누르면 중복 없이 이어서 처리해요.'; return; }
      const b = r.body as Record<string, any>;
      if (r.status >= 400) { msg.textContent = reuploadFailText(r.status, b); if (b['error'] === 'registered_but_confirm_failed') msg.textContent = String(b['note']); return; }
      if (!confirmed) {
        previewed = true;
        clear(out);
        if (b['unchanged'] === true) {
          out.append(h('p', { 'data-testid': 'reup-diff' }, `이전 버전(v${String(b['versionNo'])})과 내용이 같아요. 등록해도 새 버전이 만들어지지 않아요.`));
          if (b['evidenceLinks'] !== undefined) out.append(evidenceNode(b)); // 내용이 같아도 확인 필요 지점의 근거 유형은 고를 수 있다
          msg.textContent = '아직 저장되지 않았어요(바뀐 내용 없음).';
          return;
        }
        out.append(h('p', { 'data-testid': 'reup-diff' }, `다음 버전 ${String(b['nextVersion'])}: ${diffSummary(b['diff'] as Diff, (b['reusedFiles'] as string[]) ?? [])}`));
        const ch = ((b['diff'] as Diff | undefined)?.changedOrAdded ?? []);
        if (ch.length > 0) out.append(h('details', {}, h('summary', {}, '바뀐 지점 보기'), h('ul', {}, ...ch.map((c) => h('li', {}, `${c.section} ${c.number}번`)))));
        out.append(evidenceNode(b));
        msg.textContent = '아직 저장되지 않았어요. 아래에서 확인하고 등록해 주세요.';
        return;
      }
      msg.textContent = b['unchanged'] === true
        ? '이전 버전과 내용이 같아 새 버전을 만들지 않았어요.'
        : `새 버전 v${String(b['versionNo'])}을 등록했어요. ${String(b['note'] ?? '')} 이미 시작한 학습은 시작한 버전으로 이어져요.`;
      previewed = false; confirm.checked = false;
    } finally { busy = false; previewBtn.disabled = false; regBtn.disabled = false; }
  }

  return h('section', { 'data-testid': 'pdf-reupload' },
    h('h2', {}, '파일 재업로드로 수정'),
    h('p', {}, '바뀐 PDF만 올리면 돼요. 올리지 않은 종류는 이전 버전을 그대로 써요. 이미 푼 기록과 진행 중인 학습은 바뀌지 않고, 새로 시작하는 학생부터 새 버전을 풀어요.'),
    h('label', { class: 'review-field', for: 'reup-set' }, '교재', sel),
    ...inputs.map((x) => h('label', { class: 'review-field', for: x.id }, `${x.label} PDF(바뀐 경우만)`, x.input)),
    h('label', { class: 'review-field', for: 'reup-bulk' }, '인정 정답이 여러 개인 지점의 뜻', bulkSel),
    h('label', { class: 'review-field' }, autoEv, ' 해설 기반 근거 문제 후보를 새 버전에 함께 등록해요(교사 입력 없음, 오답 보기 타당성은 아직 검증 전)'),
    h('fieldset', { 'data-testid': 'reup-targets' }, h('legend', {}, '적용 학교·학년 변경(선택)'),
      h('p', {}, '켜지 않으면 이전 버전의 대상이 그대로 새 버전으로 옮겨져요.'), picker.node),
    previewBtn, msg, out,
    h('label', {}, confirm, ' 변경 내용을 확인했어요'), regBtn,
    h('p', {}, '확인하고 등록하면 정답 기준이 이 계정 이름으로 확인·적용돼요(원장 별도 승인 없음). 학생 배포 허용 설정은 바꾸지 않아요.'));
}
